import React, { createContext, useState, useMemo, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import InviteToJobModal from '../modals/InviteToJobModal';
import AddCandidateNoteModal from '../modals/AddCandidateNoteModal';
import SubmitCandidateModal from '../modals/SubmitCandidateModal';
import IntelCandidate from '../components/Dashboard/Candidates/CandidatesList/IntelCandidate';
import RejectCandidateModal from '../modals/RejectCandidateModal';

export const CandidatesContext = createContext({
  showAddCandidateNoteModal: () => {},
  showInviteToJobModal: () => {},
  showSubmitCandidateModal: () => {},
  showIntelCandidateModal: () => {},
  showRejectCandidateModal: () => {},
});

export default function CandidatesContextProvider({ children }) {
  const queryClient = useQueryClient();
  const [isAddCandidateNoteModalVisible, setIsAddCandidateNoteModalVisible] =
    useState(false);
  const [isInviteToJobModalVisible, setIsInviteToJobModalVisible] =
    useState(false);
  const [isIntelCandidateModalVisible, setIsIntelCandidateModalVisible] =
    useState(false);
  const [currentCandidate, setCurrentCandidate] = useState(null);
  const [refetchCandidate, setRefetchCandidate] = useState(null);
  const [isSubmitCandidateModalVisible, setIsSubmitCandidateModalVisible] =
    useState(false);
  const [submissionFlow, setSubmissionFlow] = useState('');
  const [jobPostId, setJobPostId] = useState(null);
  const [isRejectCandidateModalVisible, setIsRejectCandidateModalVisible] =
    useState(false);
  const [refetchRejected, setRefetchRejected] = useState(null);

  const showIntelCandidateModal = useCallback((arg1, arg2) => {
    let candidate;
    let refetch;

    let isOptionsPayload = false;
    if (arg1 && typeof arg1 === 'object') {
      isOptionsPayload = 'candidate' in arg1 || 'refetch' in arg1;
    }

    if (isOptionsPayload) {
      // Called with options object
      candidate = arg1.candidate;
      refetch = arg1.refetch;
    } else {
      // Called with positional args
      candidate = arg1;
      refetch = arg2;
    }

    setCurrentCandidate(candidate);
    setIsIntelCandidateModalVisible(true);
    setRefetchCandidate(() => refetch);
  }, []);

  const showAddCandidateNoteModal = useCallback((candidate, refetch) => {
    setCurrentCandidate(candidate);
    setIsAddCandidateNoteModalVisible(true);
    setRefetchCandidate(() => refetch);
  }, []);

  const showRejectCandidateModal = useCallback((arg1, arg2) => {
    // Support both positional args and an options object
    let candidate;
    let refetch;

    let isOptionsPayload = false;
    if (arg1 && typeof arg1 === 'object') {
      isOptionsPayload = 'candidate' in arg1 || 'refetch' in arg1;
    }

    if (isOptionsPayload) {
      // Called with options object
      candidate = arg1.candidate;
      refetch = arg1.refetch;
    } else {
      // Called with positional args
      candidate = arg1;
      refetch = arg2;
    }

    setCurrentCandidate(candidate);
    setIsRejectCandidateModalVisible(true);
    setRefetchRejected(() => refetch);
  }, []);

  const showInviteToJobModal = useCallback((arg1, arg2) => {
    let candidate;
    let refetch;

    let isOptionsPayload = false;
    if (arg1 && typeof arg1 === 'object') {
      isOptionsPayload = 'candidate' in arg1 || 'refetch' in arg1;
    }

    if (isOptionsPayload) {
      // Called with options object
      candidate = arg1.candidate;
      refetch = arg1.refetch;
    } else {
      // Called with positional args
      candidate = arg1;
      refetch = arg2;
    }

    setCurrentCandidate(candidate);
    setIsInviteToJobModalVisible(true);
    setRefetchCandidate(() => refetch);
  }, []);

  const showSubmitCandidateModal = useCallback((arg1, arg2, arg3, arg4) => {
    // Support both positional args and an options object
    let candidate;
    let refetch;
    let flow;
    let explicitJobPostId;

    let isOptionsPayload = false;
    if (arg1 && typeof arg1 === 'object' && !('id' in arg1)) {
      isOptionsPayload =
        'candidate' in arg1 ||
        'refetch' in arg1 ||
        'flow' in arg1 ||
        'jobPostId' in arg1;
    }

    if (isOptionsPayload) {
      // Called with options object
      candidate = arg1.candidate;
      refetch = arg1.refetch;
      flow = arg1.flow;
      explicitJobPostId = arg1.jobPostId;
    } else {
      // Called with positional args
      candidate = arg1;
      refetch = arg2;
      // previous signature could be (candidate, refetch) or (candidate, refetch, jobPostId, flow)
      if (typeof arg3 === 'string' || !arg4) {
        flow = arg3;
        explicitJobPostId = undefined;
      } else {
        explicitJobPostId = arg3;
        flow = arg4;
      }
    }

    setCurrentCandidate(candidate);
    setSubmissionFlow(flow || '');
    // Derive jobPostId if not provided explicitly
    const candidateJobPostId =
      candidate?.jobPost?.id || candidate?.jobPostId || null;
    const derivedJobPostId = explicitJobPostId || candidateJobPostId;
    setJobPostId(derivedJobPostId);
    setIsSubmitCandidateModalVisible(true);
    setRefetchCandidate(() => refetch);
  }, []);

  const value = useMemo(
    () => ({
      showAddCandidateNoteModal,
      showInviteToJobModal,
      showSubmitCandidateModal,
      showIntelCandidateModal,
      showRejectCandidateModal,
    }),
    [
      showAddCandidateNoteModal,
      showInviteToJobModal,
      showSubmitCandidateModal,
      showIntelCandidateModal,
      showRejectCandidateModal,
    ]
  );

  return (
    <CandidatesContext.Provider value={value}>
      {children}
      <AddCandidateNoteModal
        show={isAddCandidateNoteModalVisible}
        handleClose={() => {
          setIsAddCandidateNoteModalVisible(false);
          setCurrentCandidate(null);
        }}
        currentCandidate={currentCandidate}
        refetch={refetchCandidate}
      />
      <InviteToJobModal
        currentCandidate={currentCandidate}
        show={isInviteToJobModalVisible}
        handleClose={() => {
          setIsInviteToJobModalVisible(false);
          setCurrentCandidate(null);
        }}
        refetch={refetchCandidate}
      />
      <SubmitCandidateModal
        currentCandidate={currentCandidate}
        show={isSubmitCandidateModalVisible}
        handleClose={() => {
          setIsSubmitCandidateModalVisible(false);
          setCurrentCandidate(null);
          setSubmissionFlow('');
          setJobPostId(null);
        }}
        refetch={refetchCandidate}
        submissionFlow={submissionFlow}
        jobPostId={jobPostId}
      />

      <RejectCandidateModal
        currentCandidate={currentCandidate}
        show={isRejectCandidateModalVisible}
        handleClose={() => {
          setIsRejectCandidateModalVisible(false);
          setCurrentCandidate(null);
        }}
        refetch={refetchRejected}
      />

      {isIntelCandidateModalVisible && (
        <IntelCandidate
          handleClose={() => {
            setIsIntelCandidateModalVisible(false);
            setCurrentCandidate(null);
            queryClient.invalidateQueries({ queryKey: ['candidates'] });
          }}
          refetchCandidates={refetchCandidate}
          candidate={currentCandidate}
        />
      )}
    </CandidatesContext.Provider>
  );
}
